import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { Badge, Dropdown, Tooltip, useDialog, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
const variantByStatus = {
    'Published': 'success',
    'Draft': 'incomplete',
    'Archived': 'common',
    'In Review': 'incomplete',
};
const formatModifiedAt = (value) => {
    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) {
        return '';
    }
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(new Date(timestamp));
};
export const useConsentTableColumns = ({ onEdit, onEditRow, onViewConfiguration, onViewSource, removeFileFromStorage, deleteTemplateVersion, onNewVersionClick, canEditTemplate, canViewTemplateSource, canArchiveTemplate, canDeleteTemplate, canCreateTemplate, isDocGenEconsentEnabled = false, onConfirmArchival, hasDraftInLineage, }) => {
    const translate = useLocalizeMessage();
    const dialog = useDialog();
    const { addNotification } = useToastNotification();
    // Guards against a double-click firing two concurrent DELETEs for the same row -- a ref (not
    // state) is enough since this only needs to block a second call, not drive a visual re-render.
    const deleteInFlightRef = useRef(new Set());
    const archiveInFlightRef = useRef(new Set());
    const labelByStatus = useMemo(() => ({
        'Published': translate('Published'),
        'Draft': translate('Draft'),
        'Archived': translate('Archived'),
        'In Review': translate('In Review'),
    }), [translate]);
    return useMemo(() => [
        {
            header: translate('Title'),
            accessorKey: 'title',
            size: 280,
            cell: (context) => {
                const title = context.getValue();
                const { id, status, isPublishing } = context.row.original;
                // Title is the click target. Draft stays Edit. Every other status opens the
                // read-only configuration view. Compiled View source stays a kebab action.
                // New version stays on the kebab.
                const handleTitleClick = () => {
                    if (status === 'Draft') {
                        if (onEditRow) {
                            onEditRow(id);
                        }
                        else if (onEdit) {
                            onEdit(id);
                        }
                        return;
                    }
                    if (onViewConfiguration) {
                        onViewConfiguration(id);
                    }
                    else {
                        onViewSource?.(id);
                    }
                };
                // Permission is evaluated with the status branch, not after it: a Draft row without
                // update scope must not become a button just because onEditRow is wired.
                // A publishing row is still Draft, and editing it would race the publish.
                const hasClickHandler = !isPublishing &&
                    (status === 'Draft'
                        ? canEditTemplate && !!(onEditRow || onEdit)
                        : canViewTemplateSource && !!(onViewConfiguration || onViewSource));
                const titleAction = status === 'Draft'
                    ? translate('Edit {title}', { title })
                    : translate('View configuration for {title}', { title });
                return (
                // displayOnlyOnOverflow is deliberately NOT used here: it checks this Tooltip's own
                // reference element (the wrapping div) for overflow, but the actual truncation
                // happens on the nested button/span below -- the wrapper div just shrinks to match the
                // already-clipped child and never overflows itself, so that check would permanently
                // (and silently) suppress the tooltip. Always showing it on hover, like
                // study_notifications_table.component.tsx does, avoids that mismatch.
                _jsx(Tooltip, { as: 'div', title: title, placement: 'top-start', wrapperClassName: 'nucleus-block nucleus-min-w-0', children: hasClickHandler ? (_jsx("button", { type: 'button', "data-testid": 'consent-title-link', "aria-label": titleAction, 
                        // max-w-[220px] (not max-w-full) is deliberate: the Table component renders with
                        // table-layout: auto, so an unusually long title in one row otherwise stretches
                        // the whole column to fit it, and truncation never engages for that widest cell.
                        // Capping this element's own width keeps truncation working regardless of how
                        // wide the column itself grows.
                        className: 'nucleus-block max-w-[220px] nucleus-truncate nucleus-text-left nucleus-font-medium nucleus-text-primary-500 nucleus-underline nucleus-underline-offset-2 hover:nucleus-text-primary-700', onClick: handleTitleClick, children: title })) : (_jsx("span", { "data-testid": 'consent-title-link', className: 'nucleus-block max-w-[220px] nucleus-truncate', children: title })) }));
            },
        },
        {
            header: translate('Version'),
            accessorKey: 'version',
            size: 88,
            cell: (context) => {
                const version = context.getValue();
                const { status } = context.row.original;
                return (_jsx(Badge, { variant: status === 'Draft' ? 'incomplete' : 'common', label: translate('v{version}', { version: String(version) }), dataTestId: 'consent-version-badge' }));
            },
        },
        {
            header: translate('Language'),
            accessorKey: 'language',
            cell: (context) => context.getValue() || '—',
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            cell: (context) => (_jsx("span", { className: 'nucleus-block max-w-[200px] nucleus-truncate', children: context.getValue() })),
        },
        {
            header: translate('Sites'),
            id: 'sites',
            cell: (context) => {
                const { assignedSiteCount, familyParentId } = context.row.original;
                if (assignedSiteCount === undefined) {
                    return _jsx("span", { "data-testid": 'consent-sites-cell' });
                }
                // Expanded siblings: assignment is shown on the family parent. Figma uses an em dash here.
                if (familyParentId) {
                    return _jsx("span", { "data-testid": 'consent-sites-cell', children: "\u2014" });
                }
                if (assignedSiteCount === 0) {
                    return (_jsx("span", { "data-testid": 'consent-sites-cell', className: 'nucleus-text-sm nucleus-font-medium nucleus-leading-6 nucleus-text-error-600', children: translate('Not assigned') }));
                }
                return (_jsx("span", { "data-testid": 'consent-sites-cell', className: 'nucleus-text-sm nucleus-leading-6', children: assignedSiteCount }));
            },
        },
        {
            header: translate('Modified'),
            id: 'modified',
            size: 200,
            cell: (context) => {
                const { updatedAt, createdAt } = context.row.original;
                const value = updatedAt || createdAt;
                if (!value) {
                    return _jsx("span", { "data-testid": 'consent-modified-cell', children: "\u2014" });
                }
                return _jsx("span", { "data-testid": 'consent-modified-cell', children: formatModifiedAt(value) });
            },
        },
        {
            header: translate('Status'),
            accessorKey: 'status',
            cell: (context) => {
                const status = context.getValue();
                if (context.row.original.isPublishing) {
                    return _jsx(Badge, { variant: 'active', label: translate('Publishing…'), dataTestId: 'consent-publishing-badge' });
                }
                return _jsx(Badge, { variant: variantByStatus[status], label: labelByStatus[status] });
            },
        },
        {
            id: 'actions',
            size: 48,
            cell: (context) => {
                const { id, title, status, templateKey, isPublishing } = context.row.original;
                const runArchive = async () => {
                    if (archiveInFlightRef.current.has(id)) {
                        return;
                    }
                    archiveInFlightRef.current.add(id);
                    try {
                        await removeFileFromStorage(id);
                    }
                    catch {
                        addNotification({
                            type: 'error',
                            title: translate('Archive failed'),
                            subtitle: translate('Unknown error'),
                        });
                    }
                    finally {
                        archiveInFlightRef.current.delete(id);
                    }
                };
                const handleArchiveClick = async () => {
                    if (archiveInFlightRef.current.has(id)) {
                        return;
                    }
                    if (isDocGenEconsentEnabled) {
                        onConfirmArchival?.({ id, templateKey });
                        return;
                    }
                    // Flag-off: keep the legacy string confirm. Do not fetch archive-impact.
                    const confirmed = await dialog.open({
                        title: translate('Confirm Archival?'),
                        body: translate('Are you sure you want to archive this template? This is a one-way process, and you will not be able to un-archive, edit, or publish this template in the future.'),
                        actionButtons: [
                            { label: translate('Archive'), dataTestId: 'consent-archive-confirm', isDestructive: true },
                            { label: translate('Cancel'), dataTestId: 'consent-archive-cancel' },
                        ],
                        willCloseOnOutsideClickAndEscapeKeyPress: false,
                    });
                    if (!confirmed) {
                        return;
                    }
                    await runArchive();
                };
                // Edit and View both moved to the title click (see the Title column's cell above) —
                // the kebab now only holds actions that mutate/transition the resource.
                const handleDeleteClick = async () => {
                    if (deleteInFlightRef.current.has(id)) {
                        return;
                    }
                    const confirmed = await dialog.open({
                        title: translate('Delete "{title}"?', { title }),
                        body: translate('This template will no longer be visible anywhere in Studio. This action cannot be undone.'),
                        actionButtons: [
                            { label: translate('Delete'), dataTestId: 'consent-delete-confirm', isDestructive: true },
                            { label: translate('Cancel'), dataTestId: 'consent-delete-cancel' },
                        ],
                        willCloseOnOutsideClickAndEscapeKeyPress: false,
                    });
                    if (!confirmed) {
                        return;
                    }
                    deleteInFlightRef.current.add(id);
                    try {
                        await deleteTemplateVersion(id);
                    }
                    catch {
                        addNotification({
                            type: 'error',
                            title: translate('Delete failed'),
                            subtitle: translate('Unknown error'),
                        });
                    }
                    finally {
                        deleteInFlightRef.current.delete(id);
                    }
                };
                const dropdownItems = [
                    ...(canViewTemplateSource && onViewConfiguration
                        ? [
                            {
                                id: 'view-configuration',
                                content: translate('View configuration'),
                                onClick: () => onViewConfiguration(id),
                            },
                        ]
                        : []),
                    ...(canViewTemplateSource && onViewSource
                        ? [
                            {
                                id: 'view-source',
                                content: translate('View source'),
                                onClick: () => onViewSource(id),
                            },
                        ]
                        : []),
                    ...(onNewVersionClick && canCreateTemplate && !hasDraftInLineage?.(templateKey)
                        ? [
                            {
                                id: 'clone',
                                content: translate('New version'),
                                onClick: () => onNewVersionClick(id),
                            },
                        ]
                        : []),
                    // Archiving retires an active Published version; deleting (below) removes a Draft
                    // that was never published. The two are mutually exclusive by status, covering
                    // every row between them.
                    ...(status === 'Published' && canArchiveTemplate && !isPublishing
                        ? [
                            {
                                id: 'archive',
                                content: translate('Archive'),
                                onClick: () => void handleArchiveClick(),
                                destructive: true,
                            },
                        ]
                        : []),
                    // Soft delete is Draft-only -- "cannot delete a template that was at any point
                    // published" refers to the row's own status (a Published/Archived row can never be
                    // deleted), not its lineage. A Draft cloned from a previously-published sibling is
                    // still deletable.
                    ...(status === 'Draft' && canDeleteTemplate && !isPublishing
                        ? [
                            {
                                id: 'delete',
                                content: translate('Delete'),
                                onClick: () => void handleDeleteClick(),
                                destructive: true,
                            },
                        ]
                        : []),
                ];
                if (dropdownItems.length === 0) {
                    return null;
                }
                return (_jsx("section", { className: 'nucleus-flex nucleus-justify-end', children: _jsx(Dropdown, { variant: 'kebab', items: dropdownItems }) }));
            },
        },
    ], [
        translate,
        onEdit,
        onEditRow,
        onViewConfiguration,
        onViewSource,
        removeFileFromStorage,
        deleteTemplateVersion,
        onNewVersionClick,
        canEditTemplate,
        canViewTemplateSource,
        canArchiveTemplate,
        canDeleteTemplate,
        canCreateTemplate,
        isDocGenEconsentEnabled,
        onConfirmArchival,
        hasDraftInLineage,
        dialog,
        labelByStatus,
        addNotification,
    ]);
};
